import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { PlusIcon, PencilIcon, TrashIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useApiQueryArray } from '../../hooks/useApiQuery';

interface Alumno {
  id: number;
  numeroAlumno: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono?: string;
  dni?: string;
  estado: string;
  createdAt: string;
}

// Usar la configuración de axios en lugar de URL hardcodeada

const AlumnosPage: React.FC = () => {
  const [alumnos, setAlumnos] = useState<Alumno[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAlumno, setEditingAlumno] = useState<Alumno | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: number;
    errors: string[];
    alumnosImportados: any[];
  } | null>(null);
  const [formData, setFormData] = useState({
    numeroAlumno: '',
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    dni: '',
  });

  // Usar el hook personalizado para obtener alumnos
  const { data: alumnosData, isLoading: loadingAlumnos, error: alumnosError } = useApiQueryArray<Alumno>({
    url: '/alumnos',
    queryKey: ['alumnos'],
  });

  useEffect(() => {
    if (alumnosData) {
      setAlumnos(alumnosData);
    }
  }, [alumnosData]);

  // Actualizar loading state
  useEffect(() => {
    setLoading(loadingAlumnos);
  }, [loadingAlumnos]);

  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingAlumno) {
        await axios.patch(`/alumnos/${editingAlumno.id}`, formData);
        alert('Alumno actualizado exitosamente');
      } else {
        await axios.post(`/alumnos`, formData);
        alert('Alumno creado exitosamente');
      }
      setShowModal(false);
      setEditingAlumno(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['alumnos'] });
    } catch (error: any) {
      console.error('Error saving alumno:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error al guardar el alumno';
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleEdit = (alumno: Alumno) => {
    setEditingAlumno(alumno);
    setFormData({
      numeroAlumno: alumno.numeroAlumno,
      nombre: alumno.nombre,
      apellido: alumno.apellido,
      email: alumno.email,
      telefono: alumno.telefono || '',
      dni: alumno.dni || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar este alumno?')) {
      try {
        await axios.delete(`/alumnos/${id}`);
        alert('Alumno eliminado exitosamente');
        queryClient.invalidateQueries({ queryKey: ['alumnos'] });
      } catch (error: any) {
        console.error('Error deleting alumno:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Error al eliminar el alumno';
        alert(`Error: ${errorMessage}`);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      numeroAlumno: '',
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      dni: '',
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`/alumnos/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setImportResult(response.data);
      
      if (response.data.success > 0) {
        alert(`✅ Importación completada: ${response.data.success} alumnos importados`);
        if (response.data.errors && response.data.errors.length > 0) {
          console.warn('Errores durante la importación:', response.data.errors);
        }
      } else {
        alert('⚠️ No se importaron alumnos. Revisa el archivo y los errores.');
      }
      
      queryClient.invalidateQueries({ queryKey: ['alumnos'] });
    } catch (error) {
      console.error('Error importing alumnos:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.message) {
          alert(`❌ Error al importar: ${axiosError.response.data.message}`);
        } else {
          alert('❌ Error al importar alumnos. Revisa la consola para más detalles.');
        }
      } else {
        alert('❌ Error al importar alumnos. Revisa la consola para más detalles.');
      }
    } finally {
      setImporting(false);
      // Limpiar el input
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Cargando alumnos...</div>
      </div>
    );
  }

  if (alumnosError) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-2">Error al cargar alumnos</div>
          <div className="text-sm text-gray-600">{alumnosError.message}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Gestión de Alumnos</h2>
        <div className="flex gap-2">
          <label className={`cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium ${
            importing ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-gray-700 bg-white hover:bg-gray-50'
          }`}>
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            {importing ? 'Importando...' : 'Importar Excel'}
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={importing}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo Alumno
          </button>
        </div>
      </div>

      {/* Resultados de importación */}
      {importResult && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <h3 className="text-lg font-medium text-blue-900 mb-2">
            Resultados de la Importación
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-3 rounded border">
              <div className="text-2xl font-bold text-green-600">{importResult.success}</div>
              <div className="text-sm text-gray-600">Alumnos importados</div>
            </div>
            {importResult.errors.length > 0 && (
              <div className="bg-white p-3 rounded border">
                <div className="text-2xl font-bold text-red-600">{importResult.errors.length}</div>
                <div className="text-sm text-gray-600">Errores</div>
              </div>
            )}
            {importResult.alumnosImportados.length > 0 && (
              <div className="bg-white p-3 rounded border">
                <div className="text-2xl font-bold text-blue-600">{importResult.alumnosImportados.length}</div>
                <div className="text-sm text-gray-600">Total procesados</div>
              </div>
            )}
          </div>
          
          {importResult.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-red-800 mb-2">Errores encontrados:</h4>
              <div className="bg-red-50 p-3 rounded border border-red-200 max-h-32 overflow-y-auto">
                {Array.isArray(importResult.errors) && importResult.errors.map((error, index) => (
                  <div key={index} className="text-sm text-red-700 mb-1">
                    • {error}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <button
            onClick={() => setImportResult(null)}
            className="mt-4 text-sm text-blue-600 hover:text-blue-800"
          >
            Cerrar resultados
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Número
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nombre
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Teléfono
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.isArray(alumnos) && alumnos.map((alumno) => (
              <tr key={alumno.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {alumno.numeroAlumno}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {alumno.nombre} {alumno.apellido}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {alumno.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {alumno.telefono || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    alumno.estado === 'activo' ? 'bg-green-100 text-green-800' :
                    alumno.estado === 'inactivo' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {alumno.estado}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(alumno)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(alumno.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingAlumno ? 'Editar Alumno' : 'Nuevo Alumno'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Número de Alumno
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.numeroAlumno}
                      onChange={(e) => setFormData({ ...formData, numeroAlumno: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Nombre
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Apellido
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.apellido}
                      onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Teléfono
                    </label>
                    <input
                      type="text"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      DNI
                    </label>
                    <input
                      type="text"
                      value={formData.dni}
                      onChange={(e) => setFormData({ ...formData, dni: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Opcional"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingAlumno(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    {editingAlumno ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlumnosPage;
